import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload as UploadIcon, FileText, FileSpreadsheet, Sparkles, AlertCircle, CheckCircle2, AlertTriangle, Copy, ArrowLeft, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function UploadPage({ companyId }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Review step state
  const [reviewData, setReviewData] = useState(null);
  const [selectedForImport, setSelectedForImport] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [missingMonths, setMissingMonths] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dupWarning, setDupWarning] = useState(null);

  const fmtMonth = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' });
  };

  const fetchHistory = () => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/upload/history`)
      .then(res => {
        setHistory(res.data.history);
        setMissingMonths(res.data.missing_months || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/accounts`).then(res => {
      setAccounts(res.data);
      if (res.data.length > 0) setSelectedAccount(res.data[0].account_id);
    });
    fetchHistory();
    api.get('/schedule-c-categories').then(res => setCategories(res.data)).catch(() => {});
  }, [companyId]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setFile(e.dataTransfer.files[0]);
      resetReview();
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      resetReview();
    }
  };

  const resetReview = () => {
    setReviewData(null);
    setSelectedForImport(new Set());
    setImportResult(null);
    setClassifyResult(null);
    setDupWarning(null);
  };

  const handleUpload = async (force = false) => {
    if (!file || !selectedAccount) {
      toast.error("Select an account and file first");
      return;
    }
    setUploading(true);
    resetReview();
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(
        `/companies/${companyId}/upload?account_id=${selectedAccount}${force ? '&force=true' : ''}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setReviewData(res.data);
      // Pre-select all non-duplicates
      const nonDups = new Set(
        res.data.parsed.filter(t => !t.is_duplicate).map(t => t.temp_id)
      );
      setSelectedForImport(nonDups);
      toast.success(res.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "period_already_imported") {
        setDupWarning(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      }
    } finally {
      setUploading(false);
      fetchHistory();
    }
  };

  const toggleTxnSelect = (tempId) => {
    setSelectedForImport(prev => {
      const next = new Set(prev);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const selectAllNonDuplicates = () => {
    if (!reviewData) return;
    setSelectedForImport(new Set(
      reviewData.parsed.filter(t => !t.is_duplicate).map(t => t.temp_id)
    ));
  };

  const selectAll = () => {
    if (!reviewData) return;
    setSelectedForImport(new Set(reviewData.parsed.map(t => t.temp_id)));
  };

  const selectNone = () => setSelectedForImport(new Set());

  const setRowCategory = (tempId, category) => {
    setReviewData(prev => ({
      ...prev,
      parsed: prev.parsed.map(t => t.temp_id === tempId ? { ...t, category } : t)
    }));
  };

  const handleDeleteHistoryEntry = async (uploadId) => {
    try {
      await api.delete(`/companies/${companyId}/upload/history/${uploadId}`);
      toast.success("Log entry removed (imported transactions are not affected)");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete entry");
    }
  };

  const handleConfirmImport = async () => {
    if (selectedForImport.size === 0) {
      toast.error("Select at least one transaction to import");
      return;
    }
    setConfirming(true);
    try {
      const txnsToImport = reviewData.parsed
        .filter(t => selectedForImport.has(t.temp_id))
        .map(t => ({
          date: t.date,
          description: t.description,
          amount: t.amount,
          type: t.type,
          category: t.category || "uncategorized",
        }));

      const res = await api.post(`/companies/${companyId}/upload/confirm`, {
        account_id: reviewData.account_id,
        source: reviewData.source,
        source_filename: reviewData.source_filename || file?.name || "",
        upload_id: reviewData.upload_id,
        transactions: txnsToImport,
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setConfirming(false);
    }
  };

  const handleClassify = async () => {
    setClassifying(true);
    try {
      const res = await api.post(`/companies/${companyId}/classify`);
      setClassifyResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Classification failed");
    } finally {
      setClassifying(false);
    }
  };

  const handleStartOver = () => {
    setFile(null);
    resetReview();
  };

  // Review step
  if (reviewData && !importResult) {
    const dupsSelected = reviewData.parsed.filter(t => t.is_duplicate && selectedForImport.has(t.temp_id)).length;
    return (
      <div className="space-y-6 animate-fade-in-up" data-testid="upload-review">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-white">Review Transactions</h1>
            <p className="text-muted-foreground mt-1">
              {reviewData.total_parsed} parsed &middot; {reviewData.duplicate_count} duplicates detected &middot; {selectedForImport.size} selected for import
            </p>
          </div>
          <Button variant="ghost" onClick={handleStartOver} className="text-muted-foreground" data-testid="back-to-upload">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-white">{reviewData.total_parsed}</p>
                <p className="text-xs text-muted-foreground">Total parsed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'hsl(37 100% 64% / 0.1)' }}>
                <Copy className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-warning">{reviewData.duplicate_count}</p>
                <p className="text-xs text-muted-foreground">Duplicates found</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'hsl(160 100% 44% / 0.1)' }}>
                <CheckCircle2 className="w-5 h-5 text-profit" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-profit">{selectedForImport.size}</p>
                <p className="text-xs text-muted-foreground">Selected to import</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Selection controls */}
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-muted-foreground">Quick select:</span>
              <Button size="sm" variant="outline" onClick={selectAllNonDuplicates} className="h-7 text-xs border-border text-white" data-testid="select-non-duplicates">
                New only ({reviewData.new_count})
              </Button>
              <Button size="sm" variant="outline" onClick={selectAll} className="h-7 text-xs border-border text-white" data-testid="select-all-review">
                All ({reviewData.total_parsed})
              </Button>
              <Button size="sm" variant="outline" onClick={selectNone} className="h-7 text-xs border-border text-white" data-testid="select-none-review">
                None
              </Button>
              {dupsSelected > 0 && (
                <Badge variant="outline" className="border-warning/50 text-warning text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {dupsSelected} duplicate{dupsSelected > 1 ? 's' : ''} selected
                </Badge>
              )}
              <div className="ml-auto">
                <Button onClick={handleConfirmImport} disabled={confirming || selectedForImport.size === 0} data-testid="confirm-import-btn">
                  {confirming ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Importing...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Import {selectedForImport.size} Transactions</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Review Table */}
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent sticky top-0" style={{ background: 'hsl(233 30% 8%)' }}>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedForImport.size === reviewData.parsed.length}
                        onCheckedChange={() => {
                          if (selectedForImport.size === reviewData.parsed.length) selectNone();
                          else selectAll();
                        }}
                        className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider w-16">Status</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Date</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Description</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Type</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewData.parsed.map((t) => (
                    <TableRow
                      key={t.temp_id}
                      className={`border-border/30 ${t.is_duplicate ? 'bg-warning/5' : ''} ${selectedForImport.has(t.temp_id) ? 'bg-primary/5' : ''}`}
                      data-testid={`review-row-${t.temp_id}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedForImport.has(t.temp_id)}
                          onCheckedChange={() => toggleTxnSelect(t.temp_id)}
                          className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          data-testid={`review-check-${t.temp_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {t.is_duplicate ? (
                          <Badge variant="outline" className="text-xs border-warning/40 text-warning" data-testid={`dup-badge-${t.temp_id}`}>
                            <Copy className="w-3 h-3 mr-0.5" /> Dup
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs border-profit/40 text-profit">
                            New
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-white font-mono">{t.date}</TableCell>
                      <TableCell className="text-sm text-white max-w-[300px] truncate">{t.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${t.type === 'income' ? 'border-profit/40 text-profit' : 'border-loss/40 text-loss'}`}>
                          {t.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={t.category || "uncategorized"} onValueChange={(v) => setRowCategory(t.temp_id, v)}>
                          <SelectTrigger className="h-8 w-[210px] bg-secondary border-border text-white text-xs" data-testid={`review-category-${t.temp_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border max-h-[300px]">
                            {categories.map(c => (
                              <SelectItem key={c.code} value={c.code} className="text-white text-xs">
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className={`text-sm font-mono font-medium text-right ${t.type === 'income' ? 'text-profit' : 'text-loss'}`}>
                        {t.type === 'income' ? '+' : '-'}${t.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="upload-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-white">Upload Statements</h1>
        <p className="text-muted-foreground mt-1">Import bank or credit card statements</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader>
            <CardTitle className="text-base font-heading text-white">Upload Statement File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="bg-secondary border-border text-white" data-testid="upload-account-select">
                  <SelectValue placeholder="Select target account" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {accounts.map(a => (
                    <SelectItem key={a.account_id} value={a.account_id} className="text-white">
                      {a.name} ({a.account_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accounts.length === 0 && (
                <p className="text-xs text-warning mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Create an account first in the Accounts section.
                </p>
              )}
            </div>

            {/* Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              data-testid="upload-dropzone"
            >
              <input id="file-input" type="file" accept=".csv,.pdf" className="hidden" onChange={handleFileSelect} />
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'hsl(225 100% 60% / 0.1)' }}>
                  <UploadIcon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium">Drop your file here or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Supports CSV and PDF bank statements</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="border-border text-muted-foreground text-xs">
                    <FileSpreadsheet className="w-3 h-3 mr-1" /> CSV
                  </Badge>
                  <Badge variant="outline" className="border-border text-muted-foreground text-xs">
                    <FileText className="w-3 h-3 mr-1" /> PDF
                  </Badge>
                </div>
              </div>
            </div>

            {file && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary">
                {file.name?.toLowerCase().endsWith('.csv') ? <FileSpreadsheet className="w-5 h-5 text-profit" /> : <FileText className="w-5 h-5 text-primary" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setFile(null); resetReview(); }}
                  className="text-muted-foreground">Remove</Button>
              </div>
            )}

            <Button onClick={() => handleUpload()} disabled={!file || !selectedAccount || uploading} className="w-full" data-testid="upload-submit-btn">
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Parsing & Checking Duplicates...
                </>
              ) : (
                <><UploadIcon className="w-4 h-4 mr-2" /> Upload & Review</>
              )}
            </Button>

            {dupWarning && (
              <div className="p-4 rounded-lg border border-warning/40 bg-warning/5 space-y-3" data-testid="dup-import-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-warning font-medium">Statement already imported</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      A statement for <span className="text-white">{fmtMonth(dupWarning.period_month)}</span> on{" "}
                      <span className="text-white">{dupWarning.account_name}</span> was already imported
                      {dupWarning.imported_at && <> on {new Date(dupWarning.imported_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</>}
                      {dupWarning.prior_filename && <> from "{dupWarning.prior_filename}"</>}
                      {" "}({dupWarning.imported_count} transaction{dupWarning.imported_count !== 1 ? 's' : ''}).
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-warning/40 text-warning hover:text-warning/80"
                    onClick={() => handleUpload(true)} data-testid="dup-warning-proceed-btn">
                    Upload Anyway
                  </Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground"
                    onClick={() => { setDupWarning(null); setFile(null); }} data-testid="dup-warning-cancel-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {importResult && (
              <div className="p-4 rounded-lg border border-profit/30 bg-profit/5" data-testid="import-result">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-profit" />
                  <p className="text-sm text-profit font-medium">{importResult.message}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Successfully imported {importResult.imported_count} transaction{importResult.imported_count !== 1 ? 's' : ''}.
                  {importResult.auto_classified > 0 && (
                    <> AI auto-classified {importResult.auto_classified}
                    {importResult.needs_review_count > 0 && ` — ${importResult.needs_review_count} flagged for your review`}
                    . You can edit any category on the Transactions page.</>
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Classification Section */}
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader>
            <CardTitle className="text-base font-heading text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> AI Classification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Use AI to automatically classify uncategorized transactions into IRS Schedule C categories. 
              The AI analyzes transaction descriptions and suggests the most appropriate category.
            </p>

            <div className="p-4 rounded-lg bg-secondary space-y-3">
              <p className="text-sm text-white font-medium">How it works:</p>
              <div className="space-y-2">
                {[
                  "AI reads each transaction description",
                  "Matches to IRS Schedule C categories",
                  "Shows confidence score for each match",
                  "You can approve or edit each classification",
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    {step}
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleClassify} disabled={classifying} className="w-full" variant="outline" data-testid="classify-btn">
              {classifying ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                  Classifying with AI...
                </>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Classify Uncategorized Transactions</>
              )}
            </Button>

            {classifyResult && (
              <div className="p-4 rounded-lg border border-primary/30 bg-primary/5" data-testid="classify-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <p className="text-sm text-primary font-medium">{classifyResult.message}</p>
                </div>
                {classifyResult.results?.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                    <span className="text-xs text-muted-foreground">{r.category_name}</span>
                    <Badge variant="outline" className={`text-xs ${
                      r.confidence > 0.8 ? 'border-profit/50 text-profit' : 
                      r.confidence > 0.5 ? 'border-warning/50 text-warning' : 'border-loss/50 text-loss'
                    }`}>
                      {Math.round(r.confidence * 100)}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Import History */}
      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }} data-testid="import-history-card">
        <CardHeader>
          <CardTitle className="text-base font-heading text-white flex items-center gap-2">
            <History className="w-5 h-5 text-primary" /> Import History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {missingMonths.length > 0 && (
            <div className="mx-6 mb-4 p-4 rounded-lg border border-warning/30 bg-warning/5 space-y-2" data-testid="missing-months-alert">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <p className="text-sm text-warning font-medium">Missing statement months detected</p>
              </div>
              {missingMonths.map((mm) => (
                <div key={mm.account_id} className="text-xs text-muted-foreground" data-testid={`missing-months-${mm.account_id}`}>
                  <span className="text-white">{mm.account_name}: </span>
                  {mm.months.map(fmtMonth).join(", ")}
                </div>
              ))}
            </div>
          )}
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground px-6 pb-6" data-testid="import-history-empty">
              No statements uploaded yet. Your upload audit trail will appear here.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent sticky top-0" style={{ background: 'hsl(233 30% 8%)' }}>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Uploaded</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">File</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Account</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Period</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Result</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right">Parsed</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right">Duplicates</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right">Imported</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.upload_id} className="border-border/30" data-testid={`history-row-${h.upload_id}`}>
                      <TableCell className="text-sm text-muted-foreground font-mono whitespace-nowrap">
                        {new Date(h.uploaded_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      </TableCell>
                      <TableCell className="text-sm text-white max-w-[260px] truncate">{h.filename}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{h.account_name}</TableCell>
                      <TableCell className="text-sm text-white font-mono whitespace-nowrap">{fmtMonth(h.period_month)}</TableCell>
                      <TableCell>
                        {h.status === 'imported' && (
                          <Badge variant="outline" className="text-xs border-profit/40 text-profit">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Imported
                          </Badge>
                        )}
                        {h.status === 'parsed' && (
                          <Badge variant="outline" className="text-xs border-primary/40 text-primary">
                            Parsed — not imported
                          </Badge>
                        )}
                        {h.status === 'zero_activity' && (
                          <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                            Zero activity
                          </Badge>
                        )}
                        {h.status === 'parse_failed' && (
                          <Badge variant="outline" className="text-xs border-loss/40 text-loss">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Parse failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-mono text-white text-right">{h.total_parsed}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${h.duplicate_count > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{h.duplicate_count}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${h.imported_count > 0 ? 'text-profit' : 'text-muted-foreground'}`}>{h.imported_count}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-red-400"
                          title="Remove log entry (does not delete imported transactions)"
                          onClick={() => handleDeleteHistoryEntry(h.upload_id)}
                          data-testid={`delete-history-${h.upload_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSV Format Guide */}
      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
        <CardHeader>
          <CardTitle className="text-base font-heading text-white">Supported CSV Formats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: "Standard Format", cols: "Date, Description, Amount" },
              { title: "Debit/Credit Format", cols: "Date, Description, Debit, Credit" },
              { title: "Bank Export Format", cols: "Transaction Date, Description, Category, Amount" },
            ].map((f, i) => (
              <div key={i} className="p-3 rounded-lg bg-secondary">
                <p className="text-sm text-white font-medium mb-1">{f.title}</p>
                <p className="text-xs text-muted-foreground font-mono">{f.cols}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
